import React, { FC, useState } from 'react';
import TotalAmountBadges from '../../../shared/common/TotalAmountBadges';
import TotalDropDown from '../../selectionlists/TotalDropDown';
import { MATERIAL_ICON_CART } from '../../../shared/common/Button/Icon';
import { RESERVE_STATUS_CLOSED, RESERVE_STATUS_OPEN } from '../constants';
import { AuthenticationState, SpocState } from '../../../store/authentication/state';
import { LoanFormatState } from '../../../store/loanFormat/state';
import { Button, ButtonSize } from '../../../shared/common/Button';
import { ReserveListDetailOut } from '../../../store/api';
import Utils from '../../../shared/utils';
import { SendForApprovalDialog } from './SendForApprovalDialog';
import { Trans } from 'react-i18next';
import { t } from 'i18next';
import capitalize from 'lodash/capitalize';

type Props = {
    readonly reserveReportDetails: ReserveListDetailOut | undefined;
    readonly loanFormat: LoanFormatState | null;
    readonly loading: boolean;
    readonly isOrdered: boolean;
    readonly spoc: SpocState;
    readonly authentication: AuthenticationState;
    readonly proceedToCheckout: () => void;
    readonly proceedToOrder: () => void;
    readonly proceedToReopen: () => void;
    readonly sendForApproval: (userEmail: string) => Promise<void>;
    readonly sendForApprovalError?: string;
    readonly isLoadingSendForApproval: boolean;
};

const ReserveDetailHeader: FC<Props> = ({
    reserveReportDetails,
    loanFormat,
    loading,
    isOrdered,
    spoc,
    authentication,
    proceedToCheckout,
    proceedToOrder,
    proceedToReopen,
    sendForApproval,
    sendForApprovalError,
    isLoadingSendForApproval
}) => {
    const [showApproval, setShowApproval] = useState(false);
    const status = reserveReportDetails?.status;
    const summary = reserveReportDetails?.summary;
    const totalNetValue = summary?.totalNetValue ?? 0;

    return (
        <div className="reserve-detail-head-bar" data-displayname="ReserveDetailHeader">
            <ReserveDetailHeaderLeftPane
                loanFormat={loanFormat}
                reserveReportDetails={reserveReportDetails}
                loading={loading}
            />
            <span style={{ flexGrow: 1 }} />
            <TotalAmountBadges {...summary} />
            <TotalDropDown
                textTotalLine={t('reserveReports.reserveReport.viewReport.orderTotal')}
                {...summary}
                spoc={spoc}
            />

            {isOrdered ? (
                <Button
                    text={t('reserveReports.reserveReport.viewOrder')}
                    size={ButtonSize.LARGE}
                    onClick={proceedToOrder}
                />
            ) : null}

            {status === RESERVE_STATUS_CLOSED ? (
                <Button
                    text={t('reserveReports.reserveReport.viewReport.reopen')}
                    size={ButtonSize.LARGE}
                    onClick={proceedToReopen}
                />
            ) : status === RESERVE_STATUS_OPEN ? (
                <>
                    {!Utils.isUserAllowedToOrder(authentication) ? (
                        <Button
                            text={t('reserveReports.reserveReport.viewReport.sendForApproval')}
                            className="send-for-approval"
                            size={ButtonSize.LARGE}
                            icon={MATERIAL_ICON_CART}
                            onClick={() => setShowApproval(true)}
                            disabled={totalNetValue <= 0}
                        />
                    ) : null}
                    <Button
                        text={t('reserveReports.reserveReport.viewReport.proceedToCheckOut')}
                        size={ButtonSize.LARGE}
                        icon={MATERIAL_ICON_CART}
                        onClick={proceedToCheckout}
                        disabled={totalNetValue <= 0}
                    />
                </>
            ) : null}

            <SendForApprovalDialog
                show={showApproval}
                hide={() => setShowApproval(false)}
                sendForApproval={sendForApproval}
                pendingApprovalRequest={isLoadingSendForApproval}
                errorMessage={sendForApprovalError}
            />
        </div>
    );
};

export default ReserveDetailHeader;

function ReserveDetailHeaderLeftPane({
    loanFormat,
    reserveReportDetails,
    loading
}: Pick<Props, 'loanFormat' | 'reserveReportDetails' | 'loading'>) {
    return (
        <React.Fragment>
            <a onClick={() => window.history.back()} style={{ marginRight: '12px', lineHeight: 1 }}>
                <i className="material-icons bigger">arrow_back</i>
            </a>
            <span style={{ marginRight: '13px' }}>
                {loanFormat ? (
                    <Trans
                        i18nKey="reserveReports.reserveReport.viewReport.reportDate"
                        components={[<i className="bolinda-icons" />]}
                        values={{
                            icon: 'book',
                            loanFormat: t(`loanFormat.plain${capitalize(loanFormat.name.toLowerCase())}`, {
                                defaultValue: loanFormat.name
                            }),
                            date: t('dateFormat.date', {
                                val: new Date(reserveReportDetails?.date ?? 0)
                            })
                        }}
                    />
                ) : (
                    t('reserveReports.reserveReport.viewReport.reportDateWithoutLoanFormat', {
                        date: t('dateFormat.date', {
                            val: new Date(reserveReportDetails?.date ?? 0)
                        })
                    })
                )}
            </span>
        </React.Fragment>
    );
}
